import { createContext, ReactNode, useContext, useState } from 'react'
import { match } from 'ts-pattern'
import { AddKnowledgeSourceStep } from '~/features/knowledge-bases/types'
import { SelectContentStep } from './SelectContentStep'
import { SyncWebsiteContextProvider, SyncWebsiteStepState } from './SyncWebsiteStep/SyncWebsiteContext'
import { UploadDocumentStep } from './UploadDocumentStep'
import { UploadRichTextStep } from './UploadRichTextStep'

type AddKnowledgeSourceStepContextValue = {
  step: AddKnowledgeSourceStep
  changeStep: (step: AddKnowledgeSourceStep) => void
  handleNavigateBack: () => void
  renderStep: () => ReactNode
  title: string
}

type AddKnowledgeSourceStepContextProviderProps = {
  workspaceId: string
  botId: string
  kbId: string
  dsId?: string
  children: ReactNode
  footerRef: React.RefObject<HTMLDivElement>
  startStep?: AddKnowledgeSourceStep
  close: () => void
}

const AddKnowledgeSourceStepContext = createContext<AddKnowledgeSourceStepContextValue | null>(null)

export const AddKnowledgeSourceStepContextProvider = ({
  workspaceId,
  botId,
  kbId,
  dsId,
  children,
  footerRef,
  startStep,
  close,
}: AddKnowledgeSourceStepContextProviderProps) => {
  const [step, setStep] = useState<AddKnowledgeSourceStep>(startStep || 'select_content')
  const [previousStep, setPreviousStep] = useState<AddKnowledgeSourceStep>('select_content')
  const [syncWebsiteSubStep, setSyncWebsiteSubStep] = useState<SyncWebsiteStepState>('discover')

  const title = match(step)
    .with('select_content', () => 'Add Knowledge Source')
    .with('upload_documents', () => 'Upload Documents')
    .with('upload_rich_text', () => 'Upload Inline Document')
    .with('sync_website', () => (syncWebsiteSubStep === 'select' ? 'Select pages to sync' : 'Sync Website'))
    .exhaustive()

  const handleNavigateBack = () => {
    if (step === 'sync_website' && syncWebsiteSubStep === 'select') {
      setSyncWebsiteSubStep('discover')
    } else {
      changeStep(previousStep)
    }
  }

  const changeStep = (newStep: AddKnowledgeSourceStep) => {
    setPreviousStep(step)
    setStep(newStep)
    if (newStep !== 'sync_website') {
      setSyncWebsiteSubStep('discover')
    }
  }

  const renderStep = () =>
    match(step)
      .with('upload_documents', () => (
        <UploadDocumentStep close={close} workspaceId={workspaceId} botId={botId} kbId={kbId} footerRef={footerRef} />
      ))
      .with('upload_rich_text', () => (
        <UploadRichTextStep close={close} workspaceId={workspaceId} botId={botId} kbId={kbId} footerRef={footerRef} />
      ))
      .with('sync_website', () => (
        <SyncWebsiteContextProvider
          close={close}
          workspaceId={workspaceId}
          botId={botId}
          kbId={kbId}
          dsId={dsId}
          footerRef={footerRef}
          onStepChange={setSyncWebsiteSubStep}
        />
      ))
      .with('select_content', () => <SelectContentStep />)
      .exhaustive()

  return (
    <AddKnowledgeSourceStepContext.Provider value={{ step, changeStep, handleNavigateBack, renderStep, title }}>
      {children}
    </AddKnowledgeSourceStepContext.Provider>
  )
}

export const useAddKnowledgeSourceStepContext = () => {
  const context = useContext(AddKnowledgeSourceStepContext)
  if (!context) {
    throw new Error('useAddKnowledgeSourceStepContext must be used within AddKnowledgeSourceStepContextProvider')
  }
  return context
}
